import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Chip,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Group as GroupIcon,
  LinkOff as LinkOffIcon,
  AutoFixHigh as AutoFixHighIcon,
} from '@mui/icons-material';
import { useUploadWizard } from '../../../contexts/UploadWizardContext';
import { useAuth } from '../../../contexts/AuthContext';
import { AdMetadata, FileWithStatus } from '../../../types';
import { CreativeAssignmentMode } from '../../../types/upload';
import { apiService } from '../../../services/api';
import SlackUserSelector from '../../common/SlackUserSelector';
import { getDefaultGroupName, getFilenamePrefix } from '../../../utils/filename';
import { AD_LANGUAGE_OPTIONS } from '../../../constants/languages';

interface GroupCreationDialogProps {
  isOpen: boolean;
  selectedFiles: FileWithStatus[];
  onCreateGroup: (sharedMetadata: Partial<AdMetadata>) => void;
  onCancel: () => void;
  error?: string | null;
}

const GroupCreationDialog: React.FC<GroupCreationDialogProps> = ({
  isOpen,
  selectedFiles,
  onCreateGroup,
  onCancel,
  error
}) => {
  const [nameEn, setNameEn] = useState('');
  const [language, setLanguage] = useState('EN');
  const [watchers, setWatchers] = useState<string[]>([]);
  const { user } = useAuth();

  // Set default name based on first file when dialog opens or files change
  useEffect(() => {
    if (isOpen && selectedFiles.length > 0) {
      const defaultName = getDefaultGroupName(selectedFiles);
      if (defaultName && !nameEn) {
        setNameEn(defaultName);
      }
    }
  }, [isOpen, selectedFiles, nameEn]);

  const detectRatio = (file: FileWithStatus): string => {
    if (file.preview?.width && file.preview?.height) {
      const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
      const divisor = gcd(file.preview.width, file.preview.height);
      return `${file.preview.width / divisor}:${file.preview.height / divisor}`;
    }
    return '16:9'; // Default ratio
  };

  const getFileRatios = () => {
    const ratios = selectedFiles.map(file => detectRatio(file));
    const uniqueRatios = [...new Set(ratios)];
    const duplicates = ratios.filter((ratio, index) => ratios.indexOf(ratio) !== index);
    const uniqueDuplicates = [...new Set(duplicates)];
    
    return { ratios, uniqueRatios, duplicates: uniqueDuplicates };
  };

  const { uniqueRatios, duplicates } = getFileRatios();

  const handleCreate = async () => {
    if (!nameEn.trim()) return;
    
    try {
      const suggestedCreative = await apiService.getSuggestedCreativeNumber();
      onCreateGroup({
        creative_number: suggestedCreative,
        name_en: nameEn,
        language: language,
        created_by: user?.email || 'web_user',
        watchers: watchers,
      });
      
      // Reset form
      setNameEn('');
      setLanguage('EN');
      setWatchers([]);
    } catch (error) {
      console.error('Error creating group:', error);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {selectedFiles.length === 1 ? 'Set Creative Metadata' : 'Group Files as Creative'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedFiles.length === 1 
            ? 'Configure metadata for this creative'
            : `Group ${selectedFiles.length} files to share the same creative ID and metadata`
          }
        </Typography>
        
        <TextField
          label="English Name (for all ads)"
          value={nameEn}
          onChange={(e) => setNameEn(e.target.value)}
          fullWidth
          margin="normal"
          required
          helperText="This will be the creative name for all files"
        />
        
        <TextField
          label="Language"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          fullWidth
          margin="normal"
          select
        >
          {AD_LANGUAGE_OPTIONS.map((option) => (
            <MenuItem key={option.code} value={option.code}>{option.label}</MenuItem>
          ))}
        </TextField>

        {/* Slack Watchers */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Slack Watchers (optional)
          </Typography>
          <SlackUserSelector
            selectedUsers={watchers}
            onUsersChange={setWatchers}
            label="Watchers"
            placeholder="Search for Slack users..."
            helperText="Users who will be notified about ads in this creative/group"
            fullWidth
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Files to group ({selectedFiles.length}):
          </Typography>
          {selectedFiles.map((file) => (
            <Chip 
              key={file.id} 
              label={`${file.name} (${detectRatio(file)})`} 
              size="small" 
              sx={{ mr: 1, mb: 1 }}
              color={duplicates.includes(detectRatio(file)) ? "error" : "default"}
            />
          ))}
        </Box>

        {duplicates.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> Some files have duplicate ratios: {duplicates.join(', ')}
            </Typography>
            <Typography variant="body2">
              Groups should contain files with different ratios only.
            </Typography>
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Ratios detected:</strong> {uniqueRatios.join(', ')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button 
          onClick={handleCreate} 
          variant="contained"
          disabled={!nameEn.trim() || duplicates.length > 0}
        >
          {selectedFiles.length === 1 ? 'Set Creative' : 'Create Group'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const IndividualMetadataStep2: React.FC = () => {
  const { user } = useAuth();
  const { 
    state, 
    setIndividualMetadata, 
    createCreativeGroup, 
    removeFileFromGroup, 
    unassignCreativeNumber,
    getFileGroup,
    validateRatiosInGroup,
    getCreativeGroups
  } = useUploadWizard();
  
  // Removed auto-assignment on step load per requirements
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [groupCreationError, setGroupCreationError] = useState<string | null>(null);
  const [isAutoGrouping, setIsAutoGrouping] = useState(false);


  const detectRatio = (file: FileWithStatus): string => {
    if (file.preview?.width && file.preview?.height) {
      const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
      const divisor = gcd(file.preview.width, file.preview.height);
      return `${file.preview.width / divisor}:${file.preview.height / divisor}`;
    }
    return '16:9'; // Default ratio
  };

  const reserveCreativeNumbers = async (count: number): Promise<number[]> => {
    return count === 1
      ? [await apiService.getSuggestedCreativeNumber()]
      : await apiService.getReservedCreativeNumbers(count);
  };

  

  const handleFileSelect = (fileId: string, selected: boolean) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(fileId);
      } else {
        newSet.delete(fileId);
      }
      return newSet;
    });
  };

  const handleCreateGroup = () => {
    setGroupCreationError(null);
    if (selectedFiles.size === 0) {
      return;
    }

    setIsGroupDialogOpen(true);
  };

  const handleGroupCreated = (sharedMetadata: Partial<AdMetadata>) => {
    const selectedFileIds = Array.from(selectedFiles);

    // Validate ratios before creating group (only if more than one file)
    if (selectedFileIds.length > 1) {
      const validation = validateRatiosInGroup(selectedFileIds);
      if (!validation.isValid) {
        setGroupCreationError(`Cannot group files with duplicate ratios: ${validation.duplicateRatios.join(', ')}`);
        return;
      }
    }

    const result = createCreativeGroup(selectedFileIds, sharedMetadata);
    if (result.success) {
      setSelectedFiles(new Set());
      setIsGroupDialogOpen(false);
      setGroupCreationError(null);
    } else {
      setGroupCreationError(result.error || 'Failed to create group');
    }
  };

  const handleAutoGroup = async () => {
    if (isAutoGrouping) return;

    setIsAutoGrouping(true);
    setGroupCreationError(null);

    try {
      // Step 1: Group files by filename prefix (no ungrouping, we overwrite metadata)
      const prefixGroups = new Map<string, FileWithStatus[]>();

      state.files.forEach(file => {
        const prefix = getFilenamePrefix(file.name);
        if (!prefixGroups.has(prefix)) {
          prefixGroups.set(prefix, []);
        }
        prefixGroups.get(prefix)!.push(file);
      });

      // Step 2: For each prefix, split into the minimal number of groups so each group has unique ratios
      for (const [prefix, files] of prefixGroups) {
        // Bucket files by ratio
        const ratioBuckets = new Map<string, FileWithStatus[]>();
        files.forEach(file => {
          const ratio = detectRatio(file);
          if (!ratioBuckets.has(ratio)) ratioBuckets.set(ratio, []);
          ratioBuckets.get(ratio)!.push(file);
        });

        // Determine how many groups we need: the max bucket size
        const groupCount = Math.max(...Array.from(ratioBuckets.values()).map(arr => arr.length));

        // Reserve that many creative numbers
        const creativeNumbers = await reserveCreativeNumbers(groupCount);

        // Prepare groups array
        const distributed: FileWithStatus[][] = Array.from({ length: groupCount }, () => []);

        // Distribute each ratio bucket across groups round-robin
        for (const bucket of ratioBuckets.values()) {
          bucket.forEach((file, idx) => {
            const target = idx % groupCount;
            distributed[target].push(file);
          });
        }

        // Assign metadata for each group
        distributed.forEach((groupFiles, idx) => {
          const creativeNumber = creativeNumbers[idx];
          const groupName = idx === 0 ? prefix : `${prefix} ${idx + 1}`;
          groupFiles.forEach(file => {
            const metadata: AdMetadata = {
              production_id: state.productionId!,
              creative_number: creativeNumber,
              ratio: detectRatio(file),
              name_en: groupName,
              language: 'EN',
              filename: file.name,
              created_by: user?.email || 'web_user',
              watchers: [],
              duration: file.preview?.duration,
            };
            setIndividualMetadata(file.id, metadata);
          });
        });
      }

    } catch (error: unknown) {
      console.error('Error during auto-grouping:', error);
      const message = error instanceof Error && error.message ? error.message : 'Unknown error';
      setGroupCreationError(`Failed to auto-group files: ${message}`);
    } finally {
      setIsAutoGrouping(false);
    }
  };

  

  const handleGroupMenuClose = () => {
    setAnchorEl(null);
    setSelectedGroupId(null);
  };

  const handleUngroupFile = (fileId: string) => {
    removeFileFromGroup(fileId);
    handleGroupMenuClose();
  };

  const getFileStatus = (file: FileWithStatus) => {
    const metadata = state.individualMetadata.get(file.id);
    if (!metadata) return { status: 'pending', color: 'warning', icon: WarningIcon };
    
    // Check if all required fields are filled
    const hasRequiredFields = metadata.production_id && 
                             metadata.creative_number && 
                             metadata.ratio && 
                             metadata.name_en && 
                             metadata.language && 
                             metadata.created_by;
    
    return hasRequiredFields 
      ? { status: 'complete', color: 'success', icon: CheckCircleIcon }
      : { status: 'incomplete', color: 'warning', icon: WarningIcon };
  };

  const getCompletionStats = () => {
    const total = state.files.length;
    const completed = state.files.filter(file => {
      const metadata = state.individualMetadata.get(file.id);
      return metadata && metadata.production_id && metadata.creative_number && 
             metadata.ratio && metadata.name_en && metadata.language && metadata.created_by;
    }).length;
    
    return { completed, total, percentage: total > 0 ? (completed / total) * 100 : 0 };
  };

  const stats = getCompletionStats();
  const selectedFilesList = state.files.filter(file => selectedFiles.has(file.id));
  const canCreateGroup = selectedFiles.size >= 1;

  // If assigning to existing creatives, this step should be skipped by navigation, but guard-render empty state
  if (state.assignmentMode === CreativeAssignmentMode.EXISTING) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Metadata & Creative Assignment
        </Typography>
        <Alert severity="info">When assigning to existing creatives, this step is skipped. Continue to Review.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Step 3: Metadata & Creative Assignment
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Group files into creative assignments. Select multiple files to assign them the same creative ID and metadata (useful for different aspect ratios of the same creative).
      </Typography>

      {/* Progress Indicator */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">
            Creative Assignment Progress
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {stats.completed} of {stats.total} files assigned to creative groups
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={stats.percentage} 
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {/* Creative Assignment Loading - removed auto assignment */}

      {/* Auto Grouping Loading */}
      {isAutoGrouping && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Auto-grouping files by filename prefixes...
          </Typography>
        </Alert>
      )}

      {groupCreationError && !isGroupDialogOpen && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {groupCreationError}
        </Alert>
      )}

      {/* Grouping Controls */}
      <Card sx={{ mb: 3, bgcolor: 'primary.light' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Creative Assignment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select files to assign them the same creative ID and metadata
              </Typography>
              {selectedFiles.size > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {selectedFiles.size} file(s) selected
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AutoFixHighIcon />}
                onClick={handleAutoGroup}
                disabled={state.files.length === 0 || isAutoGrouping}
                size="small"
              >
                {isAutoGrouping ? 'Auto Grouping...' : 'Auto Group'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setSelectedFiles(new Set())}
                disabled={selectedFiles.size === 0}
                size="small"
              >
                Clear Selection
              </Button>
              <Button
                variant="contained"
                startIcon={<GroupIcon />}
                onClick={handleCreateGroup}
                disabled={!canCreateGroup}
                size="small"
              >
                {selectedFiles.size === 1 ? 'Set Creative' : `Create Group (${selectedFiles.size})`}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Creative Groups Display - derive from individualMetadata to avoid race with creativeGroups */}
      {getCreativeGroups().size > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Assigned Creatives
          </Typography>
          {Array.from(getCreativeGroups().entries()).map(([creativeNumber, group]) => (
            <Card key={creativeNumber} sx={{ mb: 2, border: '2px solid', borderColor: 'primary.main' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      {group.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Creative ID: {creativeNumber} | Name: {group.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Files: {group.files.length}
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => unassignCreativeNumber(creativeNumber)}>
                    Ungroup
                  </Button>
                </Box>
                <TextField
                  id={`creative-language-${creativeNumber}`}
                  label="Language"
                  value={group.files[0].metadata.language}
                  onChange={(event) => {
                    group.files.forEach(({ file, metadata }) => {
                      setIndividualMetadata(file.id, { ...metadata, language: event.target.value });
                    });
                  }}
                  select
                  fullWidth
                  margin="normal"
                  helperText="Applies to every file in this creative"
                >
                  {AD_LANGUAGE_OPTIONS.map((option) => (
                    <MenuItem key={option.code} value={option.code}>{option.label}</MenuItem>
                  ))}
                </TextField>
                {/* Visual mini-rectangles for files */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                  {group.files.map(({ file, metadata }) => (
                    <Box key={file.id} sx={{
                      width: 56,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: 'grey.100',
                      border: '1px solid',
                      borderColor: 'grey.300',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      color: 'text.secondary'
                    }}>
                      {metadata.ratio}
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* File List - show only unassigned files */}
      <Grid container spacing={2}>
        {state.files.filter(f => !state.individualMetadata.get(f.id)).map((file) => {
          const fileStatus = getFileStatus(file);
          
          const StatusIcon = fileStatus.icon;
          const group = getFileGroup(file.id);
          const isSelected = selectedFiles.has(file.id);
          
          return (
            <Grid item xs={12} md={6} key={file.id}>
              <Card sx={{ 
                border: fileStatus.status === 'complete' ? '2px solid' : '1px solid',
                borderColor: isSelected ? 'primary.main' : 
                           fileStatus.status === 'complete' ? 'success.main' : 'grey.300',
                position: 'relative'
              }}>
                <CardContent>
                  <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => handleFileSelect(file.id, e.target.checked)}
                          disabled={!!(group && group.fileIds.length > 1)} // Only disable if file is in a multi-file group, allow selection of individual files
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                  </Box>

                  {/* Hide group chip to avoid confusion when groups are derived */}

                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {file.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Size: {Math.round(file.size / 1024 / 1024 * 100) / 100} MB
                    </Typography>
                  </Box>

                  {/* No metadata shown here; assigned items are displayed under Assigned Creatives */}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                    <Chip
                      icon={<StatusIcon />}
                      label={fileStatus.status === 'complete' ? 'Ready' : 'Pending'}
                      color={fileStatus.color as any}
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Group Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleGroupMenuClose}
      >
        <MenuItem disabled>
          <Typography variant="subtitle2">Group Actions</Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          if (selectedGroupId) {
            const group = state.creativeGroups.get(selectedGroupId);
            if (group) {
              group.fileIds.forEach(fileId => handleUngroupFile(fileId));
            }
          }
        }}>
          <LinkOffIcon sx={{ mr: 1 }} />
          Ungroup All Files
        </MenuItem>
      </Menu>

      {/* Group Creation Dialog */}
      <GroupCreationDialog
        isOpen={isGroupDialogOpen}
        selectedFiles={selectedFilesList}
        onCreateGroup={handleGroupCreated}
        onCancel={() => setIsGroupDialogOpen(false)}
        error={groupCreationError}
      />



      {/* Summary */}
      {stats.total > 0 && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Next Steps
          </Typography>
          {stats.completed === stats.total ? (
            <Typography variant="body2" color="success.main">
              ✓ All files are assigned to creative groups and ready for tagging!
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Please assign the remaining {stats.total - stats.completed} file(s) to creative groups before proceeding.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default IndividualMetadataStep2;